// Display formatting for GBP location hours (dashboard
// components/gbp/location-card.tsx). Extracted to this dependency-free leaf
// module — apps/dashboard/web has no test runner, so pure logic that needs
// coverage lives here instead and is exercised by gbp-hours-format.test.ts
// under this package's `node --test` harness (see phone.ts for the same
// pattern).
//
// Google's Business Information API represents a day that is open 24 hours
// as openTime: {} (normalized to "00:00" by packages/gbp/src/normalize.ts)
// and closeTime: { hours: 24 } (normalized to "24:00"). That normalization
// is correct and untouched here — "24:00" is a legitimate way to say
// "end of day". This module's job is only to RENDER it correctly:
//   - hour 24 means midnight at the end of the day, not noon
//   - a single 00:00-24:00 period is the whole day, and should read as
//     "Open 24 hours" rather than the technically-true but useless
//     "12 AM – 12 AM"

import type { GbpDayHoursView } from './gbp-types.ts';

/** Render an "HH:MM" 24-hour time as a 12-hour clock label. Hour may be 0-24
 *  (Google uses 24 for end-of-day midnight, per closeTime: { hours: 24 }) —
 *  hour 0 and hour 24 both render as "12 AM"; hour 12 renders as "12 PM". */
export function formatGbpHoursTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const h = Number.parseInt(hStr ?? '0', 10);
  const m = Number.parseInt(mStr ?? '0', 10);
  const normalizedHour = h % 24; // 24 (end-of-day midnight) -> 0
  const am = normalizedHour < 12;
  const display = normalizedHour % 12 || 12;
  const minPart = m === 0 ? '' : `:${String(m).padStart(2, '0')}`;
  return `${display}${minPart} ${am ? 'AM' : 'PM'}`;
}

/** Render a single day's hours for display. A day open the full 24 hours
 *  (exactly one period, "00:00"-"24:00") reads as "Open 24 hours" instead of
 *  two formatted times. Multi-period days (e.g. a lunch break) still render
 *  as a comma-joined list of ranges. */
export function formatGbpHours(h: GbpDayHoursView): string {
  if (h.closed) return 'Closed';
  if (h.periods.length === 0) return 'Closed';
  if (
    h.periods.length === 1 &&
    h.periods[0]?.open === '00:00' &&
    h.periods[0]?.close === '24:00'
  ) {
    return 'Open 24 hours';
  }
  return h.periods
    .map((p) => `${formatGbpHoursTime(p.open)} – ${formatGbpHoursTime(p.close)}`)
    .join(', ');
}
